import React, { FC, useMemo, useState } from 'react';
import NextLink from 'next/link';
import { Product } from 'shared/types/product/Product';
import Image from 'frontastic/lib/image';
import { ITEM_CODE, ITEM_NAME } from 'helpers/constants/attributes';
import ColorSwatches from './swatches';
import ColorSwatchesDropdown from './colorswatchesdropdown';
import usePath from 'helpers/hooks/usePath';
import { useParams, useRouter } from 'next/navigation';
import ShopOnline from '../shop-online';
import { localeMapping } from 'helpers/constants/localeMapping';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import { NationalLight, Tiempos } from 'fonts';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';
import { sessionStorageKeys } from 'helpers/constants/sessionStorageKeys';
import Typography from 'components/commercetools-ui/atoms/typography';
import { exclusivityMapping } from 'helpers/constants/exclusivityMapping';

/**
 * Which card this tile draws. One component owns variant selection, the PDP link and click
 * handling for all three, so a fix lands everywhere at once.
 *
 *   - `grid`    — the listing card: image carousel, category, name, product code, RRP, colour dropdown.
 *   - `list`    — the same product as a table row (Product / Code / RRP), for the listing's list view.
 *   - `compact` — the CMS slider card: a single image, colour swatches, no RRP. This is the markup the
 *                 separate `product-tile/tile.tsx` used to carry.
 */
export type ProductTileLayout = 'grid' | 'list' | 'compact';

interface ProductTileProps {
  product: Product;
  /** Defaults to `grid`. */
  layout?: ProductTileLayout;
  isSearchResult?: boolean;
  isCollectionPage?: boolean;
  isCategoryPage?: boolean;
  /** Applied to the tile root — used by the SSR grid to `hidden` off-screen tiles (kept in the DOM). */
  className?: string;
}

const ProductTile: FC<ProductTileProps> = ({
  product,
  layout = 'grid',
  isSearchResult = false,
  isCollectionPage = false,
  isCategoryPage = false,
  className,
}) => {
  const { locale } = useParams();
  const localeKey = localeMapping[locale as string];
  const width = useWindowWidth();
  const { path } = usePath();
  const router = useRouter();

  /**
   * Variant selection order, and why: `selectedVariant` is what mapProduct resolved for this surface
   * — the item-code a "Product IDs" slider pinned, or the master variant on a listing. It must lead,
   * because mapProduct force-sets `masterVariantID` to the chrome variant, so seeding from that first
   * would make a pinned colourway impossible to show. The last two are fallbacks for hits mapped
   * elsewhere.
   */
  const [selectedVariant, setSelectedVariant] = useState(
    () =>
      product?.variants?.find((variant) => variant.id === product?.selectedVariant?.id) ??
      product?.variants?.find((variant) => variant.id === product?.masterVariantID) ??
      product?.variants?.[0],
  );

  const [imageHovered, setImageHovered] = useState(false);

  const productUrl = useMemo(() => {
    // `product._url` is built for the variant the tile OPENS on, so it stops being right the moment a
    // swatch is picked — clicking a tile showing brushed bronze used to open the chrome PDP. Rebuilt
    // from the selected variant, but ONLY once the selection has actually moved, so callers whose
    // products carry a `_url` from somewhere other than getPdpUrl keep the URL they had.
    const variantChanged = !!selectedVariant?.id && selectedVariant.id !== product?.selectedVariant?.id;
    const base =
      (variantChanged && product.slug
        ? getPdpUrl(product.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour)
        : product._url) ?? getPdpUrl(product.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour);

    return base + (isSearchResult ? '?sr=1' : '');
  }, [product, selectedVariant, isSearchResult]);

  const exclusiveProductNZ =
    product?.variants?.[0]?.attributes?.['exclusive-product-NZ'] ??
    product?.selectedVariant?.attributes?.['exclusive-product-NZ'];
  const exclusiveProductNZValues = useMemo(() => {
    if (!Array.isArray(exclusiveProductNZ)) return [];
    return exclusiveProductNZ.filter((p) => p && String(p).trim().toLowerCase() !== 'null');
  }, [exclusiveProductNZ]);
  const showExclusiveBadge = locale === 'nz' && exclusiveProductNZValues.length > 0;
  const exclusiveToLabel = useMemo(() => {
    if (!showExclusiveBadge) return '';
    const mappedValues = exclusiveProductNZValues.map((value) => {
      const key = String(value).trim().toUpperCase();
      return exclusivityMapping[key] || value;
    });
    return `EXCLUSIVE TO ${mappedValues.join(', ')}`;
  }, [exclusiveProductNZValues, showExclusiveBadge]);

  /**
   * The listing card remembers which listing the shopper came from, so the PDP can show the right
   * breadcrumb. Modifier-clicks keep the browser's own "open in new tab", and carry the listing in the
   * query string instead (sessionStorage would be the wrong tab's).
   */
  const handleProductTileClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.altKey) {
      return; // Let the browser handle it
    }
    e.preventDefault();
    e.stopPropagation();
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      const destinationUrl = isCategoryPage
        ? productUrl + '?category=' + path
        : isCollectionPage
        ? productUrl + '?collection=' + path
        : productUrl;
      window.open(`/${locale}${destinationUrl}`, '_blank');
    } else {
      sessionStorage.setItem(sessionStorageKeys.categoryLink, '');
      sessionStorage.setItem(sessionStorageKeys.collectionLink, '');
      if (isCategoryPage) sessionStorage.setItem(sessionStorageKeys.categoryLink, path);
      else if (isCollectionPage) sessionStorage.setItem(sessionStorageKeys.collectionLink, path);
      router.push(`/${locale}${productUrl}`);
    }
  };

  const PriceDetails: React.FC<{ item: any }> = ({ item }) => {
    const countryCode = locale === 'au' ? 'AUD' : 'NZD';
    const priceValue = item?.prices?.[countryCode]?.priceValues?.[0]?.value;
    return (
      <div className="flex items-center gap-5 text-14 md:text-11 2xl:text-14">
        <Typography className="whitespace-normal">{priceValue ? (priceValue / 100).toFixed(2) : ''}</Typography>
      </div>
    );
  };

  const images11 =
    selectedVariant?.images && selectedVariant.images.length > 0
      ? selectedVariant.images
      : ['/images/no-image-placeholder/product-thumbnail.jpg'];

  const [currentIndex, setCurrentIndex] = useState(0);

  const prevImage = () => {
    setCurrentIndex((prev) => (prev === 0 ? images11.length - 1 : prev - 1));
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev === images11.length - 1 ? 0 : prev + 1));
  };

  const categoryLabel =
    product?.categories &&
    (product?.categories[0]?.name || product?.categories?.[1]?.categoryId || product?.categories[0]?.categoryId);

  // The Tūroa macron is missing from the catalogue, so it is restored on display.
  const displayName = useMemo(() => {
    const raw = product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name;
    return typeof raw === 'string' && raw.toLowerCase().includes('turoa') ? raw.replace(/turoa/gi, 'Tūroa') : raw;
  }, [product, localeKey]);

  const compactButtonClass = useMemo(() => {
    if (path.includes('/renovation-guide')) {
      return width !== null && width < 361 ? 'flex-col' : 'items-center justify-between';
    }
    return 'items-center justify-between';
  }, [path, width]);

  return (
    <div className={className}>
      <NextLink href={`/${locale}${productUrl}`} prefetch={layout !== 'compact'}>
        {/* TILE - VIEW
        --------------------- */}
        {layout === 'grid' && (
          <div onClick={handleProductTileClick}>
            <div className="relative">
              <div className="relative w-full" onMouseEnter={() => setImageHovered(true)} onMouseLeave={() => setImageHovered(false)} >
                <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem] bg-white" style={{ paddingBottom: '100%' }}>
                    {showExclusiveBadge && (
                      <span className="absolute left-8 top-8 z-10 bg-[#A6C9CC] px-8 py-2 text-[10px] font-medium text-[#000000] lg:text-xs">
                        {exclusiveToLabel}
                      </span>
                    )}
                    {/* Image */}
                    <Image
                      priority
                      src={encodeURI(images11[currentIndex].toString())}
                      alt={product.name ?? ''}
                      style={{ objectFit: 'contain', objectPosition: 'center' }}
                      className="w-full rounded-sm group-hover:opacity-75"
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      onError={(event: any) => {
                        event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                        event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                      }}
                    />

                    {/* Prev arrow */}
                    {images11.length > 1 && (
                      <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        prevImage();
                      }}
                        className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-white text-black p-4 rounded-full text-3xl"
                        aria-label="Previous image"
                        style={{ lineHeight: 1 }}
                      >
                        &lt;
                      </button>
                    )}

                    {/* Next arrow */}
                    {images11.length > 1 && (
                      <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        nextImage();
                      }}
                        className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-white text-black p-4 rounded-full text-3xl"
                        aria-label="Next image"
                        style={{ lineHeight: 1 }}
                      >
                        &gt;
                      </button>
                    )}
                </div>
              </div>
            </div>

            <div
              className={`h-[12rem] ${
                imageHovered ? 'bg-[#D9D9D9]' : isCollectionPage ? 'bg-white' : 'bg-[#F8F8F8]'
              } ] 2xl:px-8 xl:px-8 lg:px-8 md:px-8 px-16 pt-16 hover:bg-[#D9D9D9]`}
            >

              <div className="flex flex-col h-[8rem] flex-col justify-between 2xl:h-[9rem]">
                <div className="flex items-center justify-between">
                <span
                  className={`text-sm md:text-base 2xl:text-lg font-normal leading-6 text-[#000000] ${NationalLight.className}`}
                >
                    {categoryLabel}
                  </span>

                  <span className="flex cursor-pointer font-normal leading-6 text-[#333]"
                    onClick={(e) => e.preventDefault()}>
                    <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE]?.toString()} />
                  </span>
                </div>
                <div className="flex items-center justify-between">

                </div>
                <div className="block max-w-[80%]  md:max-w-[100%] ">
                  <p className={`line-clamp-2 text-14 font-medium capitalize leading-[1.625rem] text-[#2B2C39] md:text-12 lg:text-primary-black  2xl:text-14 ${Tiempos.className}`}>
                    {displayName}
                  </p>
                </div>

                  <div className="mt-auto">
                    <div className={`mt-3 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0 items-center justify-between text-12 font-medium text-[#1C1C1C] md:text-14 2xl:text-16 ${NationalLight.className}`}>
                      <span>
                        <span className="text-[#000000] text-18">Product code</span>{' '}
                        <strong>{product?.attributesAlgolia?.[ITEM_CODE] || selectedVariant?.attributes?.[ITEM_CODE]}</strong>
                      </span>
                    </div>

                    <div className={`relative z-10 mt-3 flex gap-2 items-center justify-between text-11 font-medium text-[#1C1C1C] md:text-13 2xl:text-14 ${NationalLight.className}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700">RRP $</span>
                      <PriceDetails item={selectedVariant} />
                    </div>

                        {product?.variants && (
                          <span onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <ColorSwatchesDropdown
                              product={product}
                              variants={product?.variants}
                              setSelectedVariant={setSelectedVariant}
                              viewMode={"list"}
                            />
                          </span>
                        )}
                    </div>
                  </div>
              </div>
            </div>
          </div>
        )}


{/* ------------------------------------------------------------------------------------------------------------------------------------------------------------------------
------------------------------------------------------------------------------------------------------------------------------------------------------------------------ */}


        {/* LIST - VIEW
        ------------------------- */}
        {layout === 'list' && (
          <div className="flex flex-wrap lg:flex-nowrap bg-white hover:bg-gray-100 border-t border-gray-300 w-full mb-5">
            {/* Product Name */}
            <div className="px-6 py-4 border-gray-300 text-sm text-gray-900 flex items-center gap-4 w-full lg:w-[60%]">
              {/* Image */}
              <div className="flex-shrink-0 w-90 h-90 relative">
                {showExclusiveBadge && (
                  <div className="absolute bottom-1 left-1 z-10 bg-[#A6C9CC] px-1.5 py-[2px] w-full flex items-center justify-center">
                    <span className="block text-[7px] leading-[8px] font-semibold text-black uppercase tracking-tight">
                      {exclusiveToLabel}
                    </span>
                  </div>
                )}
                <Image
                  priority
                  src={
                    selectedVariant?.images?.[0]
                      ? encodeURI(selectedVariant?.images?.[0].toString())
                      : '/images/no-image-placeholder/product-thumbnail.jpg'
                  }
                  alt={product.name ?? ''}
                  width={264}
                  height={264}
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                  className="rounded-sm group-hover:opacity-75"
                  sizes="64px"
                  onError={(event: any) => {
                    event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                    event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                  }}
                />
              </div>

              {/* Text Section */}
              <div className="flex flex-col justify-center">
                {/* Top: Category */}
                <div className={`text-sm font-normal text-gray-700 leading-5 ${NationalLight.className}`}>
                  {categoryLabel}
                </div>

                {/* Bottom: Product Name */}
                <div className="text-base font-medium text-gray-900 leading-6">
                  {product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name}
                </div>

                <div className={`mt-3 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0 items-center justify-between text-12 font-medium text-[#1C1C1C] md:text-14 2xl:text-16 ${NationalLight.className}`}>
                  <span className="flex ml-2 gap-8">
                    {product?.variants && (
                      <span onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <ColorSwatchesDropdown
                          product={product}
                          variants={product?.variants}
                          setSelectedVariant={setSelectedVariant}
                          viewMode={"list"}
                        />
                      </span>
                    )}

                    <span className="flex cursor-pointer font-normal leading-6 text-[#333]"
                      onClick={(e) => e.preventDefault()}>
                      <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE]?.toString()} />
                    </span>

                  </span>
                </div>
              </div>


            </div>

            {/* Product Code */}
            <div className="px-10 py-4 border-gray-300 text-sm text-gray-900 align-middle whitespace-nowrap w-full lg:w-[20%] flex items-center">
              {product?.attributesAlgolia?.[ITEM_CODE] || selectedVariant?.attributes?.[ITEM_CODE]}
            </div>

            {/* RRP */}
            <div className="px-10 py-4 text-sm text-gray-900 align-middle whitespace-nowrap w-full lg:w-[20%] flex items-center text-center">
              $<PriceDetails item={selectedVariant} />
            </div>
          </div>
        )}


{/* ------------------------------------------------------------------------------------------------------------------------------------------------------------------------
------------------------------------------------------------------------------------------------------------------------------------------------------------------------ */}


        {/* COMPACT - VIEW (the CMS slider card)
        ------------------------- */}
        {layout === 'compact' && (
          <div>
            <div className="relative">
              <div
                className="relative w-full"
                onMouseEnter={() => setImageHovered(true)}
                onMouseLeave={() => setImageHovered(false)}
              >
                <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem]" style={{ paddingBottom: '100%' }}>
                  {showExclusiveBadge && (
                    <span className="absolute left-8 top-8 z-10 rounded-[10px] bg-[#A6C9CC] px-8 py-2 text-[10px] font-medium text-[#000000] lg:text-xs">
                      {exclusiveToLabel}
                    </span>
                  )}
                  <Image
                    priority
                    src={
                      selectedVariant?.images?.[0]
                        ? encodeURI(selectedVariant?.images?.[0].toString())
                        : '/images/no-image-placeholder/product-thumbnail.jpg'
                    }
                    alt={product.name ?? ''}
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                    className="w-full rounded-sm group-hover:opacity-75 "
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    onError={(event: any) => {
                      event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                      event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className={`h-[12rem] ${
                imageHovered ? 'bg-[#EDEDED]' : isCollectionPage ? 'bg-white' : 'bg-[#F8F8F8]'
              } ] 2xl:px-24 xl:px-24 lg:px-24 md:px-24  px-16  pt-16 hover:bg-[#EDEDED]`}
            >
              <div className="flex h-[8rem] flex-col justify-between 2xl:h-[9rem]">
                <div className="flex items-center justify-between">
                  <span className={`text-11 font-normal leading-6 text-[#000000] md:text-12 2xl:text-14 ${NationalLight.className}`}>
                    {categoryLabel}
                  </span>
                </div>

                {/* ShopOnline is only shown for Methven NZ; the component decides. */}
                <div className="flex items-center justify-between">
                  <span className="flex cursor-pointer text-14 font-normal leading-6 text-[#333] md:text-12 2xl:text-14"
                    onClick={(e) => e.preventDefault()}>
                    <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE]?.toString()} />
                  </span>
                </div>

                <div className="block max-w-[80%]  md:max-w-[100%] ">
                  <p className={`line-clamp-2 text-18 font-medium capitalize leading-[1.625rem] text-[#2B2C39] md:text-16 lg:text-primary-black  2xl:text-18 ${Tiempos.className}`}>
                    {product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name}
                  </p>
                </div>
                <div className={`mt-4 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0  ${compactButtonClass} text-16 font-medium text-[#1C1C1C] md:text-16 2xl:text-18 `}>
                  <span>
                    <span className={`text-[#000000] ${NationalLight.className}`}>Product code</span>{' '}
                    {selectedVariant?.attributes?.[ITEM_CODE] || product?.attributesAlgolia?.[ITEM_CODE]}
                  </span>
                </div>
                <div>
                  <span className="flex gap-8">
                    {product?.variants && (
                      <ColorSwatches
                        product={product}
                        variants={product?.variants}
                        setSelectedVariant={setSelectedVariant}
                      />
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </NextLink>
    </div>
  );
};

export default ProductTile;
